import {
  STREAMING_TRANSCRIPTION_PROVIDER_IDS,
  TRANSCRIPTION_LABELS,
  TRANSCRIPTION_PROVIDER_IDS,
  type TStreamingTranscriptionProvider,
  type TTranscriptionProvider,
} from "@lexicanon/shared"
import type { MessageDescriptor } from "@lingui/core"
import { msg } from "@lingui/core/macro"

// Transcription provider lists + labels derive from the canonical shared catalog
// (4.6) so the settings UI can never drift from the backend adapter set.
export type TBatchAdapter = TTranscriptionProvider
export type TStreamingAdapter = TStreamingTranscriptionProvider

// Circuit-breaker health state per transcription provider, surfaced as a live
// badge on the Provider-order panel. Mirrors the backend circuit-breaker states
// (`closed` healthy, `half-open` cooling down, `open` failing). A provider that
// hasn't run since the pod started is simply absent from the health map — the
// panel renders those as "No data yet".
export type TCircuitState = "closed" | "open" | "half-open"

// Default batch order = benchmark-quality ranking (soniox-first, Jun 2026 NL
// benchmark), kept in sync with the backend DEFAULT_BATCH_FALLBACK_ORDER so the
// settings UI shows the same default an unconfigured org actually runs. Any
// catalog backend not listed here is appended, so a newly added provider can
// never silently drop out of the picker.
const BATCH_DEFAULT_ORDER: TBatchAdapter[] = [
  "soniox",
  "mai",
  "assemblyai",
  "speechmatics",
  "azure-llm",
  "deepgram",
  "azure",
  "local",
]
export const BATCH_ADAPTERS: TBatchAdapter[] = [
  ...BATCH_DEFAULT_ORDER,
  ...TRANSCRIPTION_PROVIDER_IDS.filter((id) => !BATCH_DEFAULT_ORDER.includes(id)),
]
// Default streaming order mirrors the backend DEFAULT_STREAMING_FALLBACK_ORDER
// (soniox-first). Any streaming-capable backend not listed is appended.
const STREAMING_DEFAULT_ORDER: TStreamingAdapter[] = ["soniox", "speechmatics", "deepgram", "assemblyai"]
export const STREAMING_ADAPTERS: TStreamingAdapter[] = [
  ...STREAMING_DEFAULT_ORDER.filter((id) => STREAMING_TRANSCRIPTION_PROVIDER_IDS.includes(id)),
  ...STREAMING_TRANSCRIPTION_PROVIDER_IDS.filter((id) => !STREAMING_DEFAULT_ORDER.includes(id)),
]

export const ADAPTER_LABELS: Record<string, string> = { ...TRANSCRIPTION_LABELS }

/**
 * Quality guidance per transcription backend, from internal Dutch-language
 * meeting benchmarks (Jun 2026): full recordings run through every backend and
 * scored on accuracy / fluency / diarization. Surfaced on the provider cards so
 * operators can pick a sensible default + fallback order. `local` is on-device
 * and was not part of the cloud benchmark.
 */
export interface ITranscriptionQuality {
  /** 1 (limited) … 5 (best) — drives the badge tone + label. */
  readonly tier: 1 | 2 | 3 | 4 | 5
  readonly note: MessageDescriptor
}

export const TRANSCRIPTION_QUALITY: Partial<Record<string, ITranscriptionQuality>> = {
  soniox: { tier: 5, note: msg`Best accuracy on Dutch and proper nouns in our benchmark.` },
  mai: { tier: 3, note: msg`Preview, no service guarantee yet. Did well on Dutch in our first tests.` },
  assemblyai: {
    tier: 4,
    note: msg`Universal-3.5 Pro. Strong readable transcripts and second-best speaker turns in our tests.`,
  },
  speechmatics: { tier: 3, note: msg`Solid all-round; reliable streaming, some fragmentation.` },
  "azure-llm": {
    tier: 3,
    note: msg`Best speaker turns in our tests. Weaker Dutch wording, so use it for who-spoke rather than as the main transcript.`,
  },
  deepgram: { tier: 2, note: msg`Fastest, but weaker on Dutch names and figures.` },
  azure: { tier: 2, note: msg`Budget option; weaker diarization on Dutch audio.` },
}

/** Badge tone + short label per quality tier (semantic palettes only). */
export const TRANSCRIPTION_QUALITY_TIERS: Record<1 | 2 | 3 | 4 | 5, { color: string; label: MessageDescriptor }> = {
  5: { color: "success", label: msg`Recommended` },
  4: { color: "brand", label: msg`Great` },
  3: { color: "info", label: msg`Good` },
  2: { color: "warning", label: msg`Basic` },
  1: { color: "warning", label: msg`Limited` },
}

/**
 * Sentinel the backend GET returns in place of any stored secret value
 * (see backend/src/modules/settings/index.ts → REDACTED_SECRET). It signals
 * "a value is configured" without leaking it. The client never holds the real
 * key, so it must:
 *   - render a "Configured" affordance instead of the literal sentinel,
 *   - disable per-provider "Test" (you can't test a key you don't have),
 *   - and skip it on save (the backend already ignores it, but the UI must not
 *     pretend a real change happened).
 * Defined here as the single source of truth — never re-typed as a string
 * literal elsewhere in the settings UI.
 */
export const REDACTED_SECRET = "__redacted__"

/**
 * Human-readable labels for the raw snake_case setting keys surfaced in the
 * super-admin company-override table. Keys not present here fall back to their
 * raw form (shown as secondary monospace text). Mirrors the provider catalogs
 * so the override page reads like the rest of settings, not like a DB dump.
 */
export const SETTING_KEY_LABELS: Record<string, string> = {
  platform_anthropic_key: "Anthropic (Claude) API key",
  platform_openrouter_key: "OpenRouter API key",
  platform_openai_key: "OpenAI API key",
  platform_runware_image_key: "Runware image API key",
  org_anthropic_key: "Anthropic (Claude) API key",
  org_openrouter_key: "OpenRouter API key",
  org_openai_key: "OpenAI API key",
  org_deepgram_key: "Deepgram API key",
  org_assemblyai_key: "AssemblyAI API key",
  org_azure_key: "Azure Speech API key",
  "org_azure-llm_key": "Azure LLM-speech API key",
  org_azure_region: "Azure Speech region",
  org_mai_region: "MAI-Transcribe-2 region",
  platform_mai_region: "MAI-Transcribe-2 region",
  org_speechmatics_key: "Speechmatics API key",
  org_soniox_key: "Soniox API key",
  org_runware_image_key: "Runware image API key",
  deepgram_key: "Deepgram API key",
  assemblyai_key: "AssemblyAI API key",
  azure_key: "Azure Speech API key",
  "azure-llm_key": "Azure LLM-speech API key",
  azure_region: "Azure Speech region",
  mai_region: "MAI-Transcribe-2 region",
  speechmatics_key: "Speechmatics API key",
  soniox_key: "Soniox API key",
  teams_tenant_id: "Microsoft Teams tenant ID",
  teams_app_id: "Microsoft Teams app (client) ID",
  teams_secret: "Microsoft Teams client secret",
  teams_watched_user_ids: "Microsoft Teams watched user IDs",
  teams_webhook_url: "Microsoft Teams channel webhook URL",
  system_prompt: "Default analysis prompt",
  image_provider: "Image provider",
  image_model: "Image model",
  visual_generation_enabled: "Generate visuals",
  visual_style: "Visual style",
  summary_layout: "Meeting summary layout",
  default_organization_id: "Default organization ID",
  batch_fallback_order: "Batch transcription fallback order",
  streaming_fallback_order: "Streaming transcription fallback order",
  default_language: "Default meeting language",
  default_ui_locale: "Default interface language",
  org_custom_vocab: "Custom vocabulary",
  speaker_confirm_threshold: "Speaker-confirmation threshold",
  default_speaker_confirm_threshold: "Default speaker-confirmation threshold",
  auto_identify_speakers: "Auto-identify speakers",
  default_auto_identify_speakers: "Auto-identify speakers (default)",
  echo_suppression_enabled: "Echo suppression",
  default_echo_suppression_enabled: "Echo suppression (default)",
  diarization_provider: "Speaker turns from",
  default_diarization_provider: "Default speaker turns provider (platform)",
  transcription_mode: "Finished transcript: engines",
  default_transcription_mode: "Finished transcript: engines (platform)",
  ensemble_merge_mode: "Finished transcript: merge behaviour",
  default_ensemble_merge_mode: "Finished transcript: merge behaviour (platform)",
  ensemble_word_engines: "Finished transcript: words engines",
  default_ensemble_word_engines: "Finished transcript: words engines (platform)",
  ensemble_turn_engines: "Finished transcript: speaker-turns engines",
  default_ensemble_turn_engines: "Finished transcript: speaker-turns engines (platform)",
  ensemble_adjudicate_speakers: "Finished transcript: AI decides disputed speakers",
  default_ensemble_adjudicate_speakers: "Finished transcript: AI decides disputed speakers (platform)",
  ensemble_headcount_tie: "Finished transcript: headcount tie",
  default_ensemble_headcount_tie: "Finished transcript: headcount tie (platform)",
  transcript_cleanup_enabled: "Clean up transcripts",
  default_transcript_cleanup_enabled: "Clean up transcripts (default)",
  auto_translate_to: "Auto-translate target languages",
  translation_adapter: "Translation adapter",
  meeting_cost_hourly_rate_eur: "Blended hourly rate (EUR)",
  byok_enabled: "Bring-your-own-key mode",
  org_encryption_at_rest: "Encryption at rest",
  last_budget_reset_month: "Last budget reset (month)",
  final_analysis_input_budget: "Meeting summary input budget",
  default_final_analysis_input_budget: "Meeting summary input budget (default)",
  quick_analysis_input_budget: "Live notes input budget",
  default_quick_analysis_input_budget: "Live notes input budget (default)",
  speaker_resolution_input_budget: "Speaker matching input budget",
  default_speaker_resolution_input_budget: "Speaker matching input budget (default)",
  // Three-tier model selection (see model-tiers.ts). The tier defaults plus one
  // key per LLM action, so the override table reads them as "Model · <task>"
  // rather than raw task_model_* snake_case.
  default_model: "Default model (standard tier)",
  light_model: "Light model (background tasks)",
  heavy_model: "Heavy model (report upgrades)",
  task_model_quick_analysis: "Model · Live notes",
  task_model_jargon_detection: "Model · Jargon detection",
  task_model_translation: "Model · Translation",
  task_model_classify_report_fit: "Model · Report matching",
  task_model_transcript_clean: "Model · Transcript clean-up",
  task_model_chapter_clustering: "Model · Chapter detection",
  task_model_speaker_resolution: "Model · Speaker matching",
  task_model_final_analysis: "Model · Meeting summary",
  task_model_meeting_qa: "Model · Ask answers",
  task_model_generate_report: "Model · Report writing",
  task_model_material_extract: "Model · File extract",
  task_model_material_digitize: "Model · Diagram from photo",
  task_model_transcript_merge_words: "Model · Transcript merge (words)",
  task_model_transcript_merge_speakers: "Model · Transcript merge (speakers)",
  reports_best_model_enabled: "Allow the heavy model for reports",
}

export const BUDGET_ADAPTER_LABELS: Record<string, string> = {
  anthropic: "Anthropic (Claude)",
  openrouter: "OpenRouter",
  openai: "OpenAI",
  deepgram: "Deepgram",
  assemblyai: "AssemblyAI",
  azure: "Azure Speech",
  mai: "MAI-Transcribe-2",
  "azure-llm": "Azure LLM-speech",
  soniox: "Soniox",
  speechmatics: "Speechmatics",
  runware: "Runware",
}

export function parseFallbackOrder<T extends string>(raw: string | undefined, defaults: T[]): T[] {
  if (!raw) return defaults
  try {
    const parsed: unknown = JSON.parse(raw)
    if (Array.isArray(parsed) && parsed.every((item) => typeof item === "string")) {
      // Keep the saved order, drop providers no longer offered, and append any
      // newly-available ones (e.g. a backend that gained streaming support after
      // the order was saved) so the picker always shows the full current set.
      const saved = [...new Set((parsed as T[]).filter((item) => defaults.includes(item)))]
      return [...saved, ...defaults.filter((d) => !saved.includes(d))]
    }
  } catch {
    // fall through to default
  }
  return defaults
}
